/* eslint-disable import/no-relative-packages, import/no-extraneous-dependencies */
import {configureStore, StateFromReducersMapObject, ThunkAction, Action} from '@reduxjs/toolkit';
import {AuthorizationFetcher, LocaleUtils} from '@wallaby-ui/core';
import {skCoreMessages, csCoreMessages, enCoreMessages, deCoreMessages, plCoreMessages} from '@wallaby-ui/layout';
import {createBrowserHistory} from 'history';
import {combineReducers} from 'redux';
import {createReduxHistoryContext} from 'redux-first-history';

import i18nConfig from './config/common/i18n_config.json';
import navigationConfig from './config/common/navigation_config';
import userSettingsConfig from './config/common/user_settings_config.json';
import hospitalizationsDetailDocumentsListGridConfig from './config/feature/hospitalization_detail/documents_list_grid_config.json';
import chartConfig from './config/feature/hospitalization_detail/lab_report_chart_config.json';
import hospitalizationsDetailTimelineGridConfig from './config/feature/hospitalization_detail/timeline_grid_config.json';
import hospitalizationsFiltersConfig from './config/feature/hospitalizations_list/filters_config.json';
import hospitalizationsListGridConfig from './config/feature/hospitalizations_list/hospitalizations_list_grid_config.json';
import csMessages from './config/locale/cs.json';
import deMessages from './config/locale/de.json';
import enMessages from './config/locale/en.json';
import plMessages from './config/locale/pl.json';
import skMessages from './config/locale/sk.json';
import createReducer from './reducers';

const thunkConfig = {
  extraArgument: {
    config: {
      common: {
        navigation: navigationConfig,
        i18n: i18nConfig,
        userSettings: userSettingsConfig,
      },
      feature: {
        hospitalizations: {
          list: {
            filters: hospitalizationsFiltersConfig,
            grid: hospitalizationsListGridConfig,
          },
        },
        hospitalizationsDetail: {
          list: {
            grid: hospitalizationsDetailDocumentsListGridConfig,
          },
          timeline: {
            grid: hospitalizationsDetailTimelineGridConfig,
          },
          chart: {
            labResult: chartConfig,
          },
        },
      },
      locales: {
        core: {
          cs: csCoreMessages,
          de: deCoreMessages,
          en: enCoreMessages,
          sk: skCoreMessages,
          pl: plCoreMessages,
        },
        application: {
          cs: csMessages,
          de: deMessages,
          en: enMessages,
          sk: skMessages,
          pl: plMessages,
        },
      },
    },
  },
};

const initializeStore = () => {
  const {createReduxHistory, routerMiddleware, routerReducer} = createReduxHistoryContext({
    history: createBrowserHistory(),
  });

  const store = configureStore({
    reducer: combineReducers(createReducer(routerReducer)),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: (() => {
          thunkConfig.extraArgument.config.common.i18n = LocaleUtils.detectAndUpdateConfigPreferredLanguage(
            i18nConfig,
            i18nConfig.config.usedLanguages,
            (language, config) => {
              // eslint-disable-next-line no-param-reassign
              config.config.preferredLanguage = language;
            },
            i18nConfig.config.preferredLanguage
          );
          return thunkConfig;
        })(),
        // TODO: turned off serializable checks for now as old code contains multiple cases of storing non-serializable data
        serializableCheck: false,
      }).concat(routerMiddleware),
  });

  store.asyncReducers = {};
  store.injectReducer = (key, reducer) => {
    store.asyncReducers[key] = reducer;
    store.replaceReducer(combineReducers(createReducer(routerReducer, store.asyncReducers)));
    return store;
  };

  // init fetcher interceptor
  AuthorizationFetcher.initStore(store);

  const history = createReduxHistory(store);

  return {store, history};
};

export type AppStore = ReturnType<typeof initializeStore>['store'];

export type AppDispatch = AppStore['dispatch'];

export type RootState = StateFromReducersMapObject<ReturnType<typeof createReducer>>;

type ExtraArgument = typeof thunkConfig.extraArgument;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, ExtraArgument, Action<string>>;

export default initializeStore;
